import {
  analysisStatusColor,
  analysisStatusLabels,
  sampleStatusColor,
  sampleStatusLabels,
  sampleTypeLabels,
} from "@/utils/labels";

// Sample analysis header summary row extracted for reuse.
interface SampleInfoCollapsedHeaderProps {
  sampleInfoSummary: {
    sampleId: string;
    name: string;
    analysisStatus: string;
    /** 좌측 사이드바를 접어도 남아야 하는 고유 정보 (아래 주석 참조). */
    sampleType?: string;
    collectionLocation?: string | null;
    sampleStatus?: string;
  } | null;
  analysisId: string | undefined;
}

/** 칩 공통 — 헤더는 가로가 좁으므로 배지 톤을 통일하고 폭만 다르게 준다. */
const CHIP =
  "px-1.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200";

export default function SampleInfoCollapsedHeader({
  sampleInfoSummary,
  analysisId,
}: SampleInfoCollapsedHeaderProps) {
  return (
    <div
      data-testid="sample-info-collapsed-header"
      className="flex-1 flex items-center gap-3 min-w-0"
    >
      <h1 className="text-xl font-bold text-gray-900 whitespace-nowrap">
        분석 대시보드
      </h1>
      {sampleInfoSummary && (
        <div className="flex items-center gap-2 text-sm text-gray-600 truncate">
          <span className="font-mono bg-gray-100 px-2 py-0.5 rounded text-xs">
            {sampleInfoSummary.sampleId}
          </span>
          <span className="truncate">{sampleInfoSummary.name}</span>
          {/* 2026-08-28: completed 가 아니면 **영문 원값 + 노란색**이었다. 노란색은
              이 화면에서 '실행중' 색이라 실패한 분석이 아직 도는 중으로 읽혔다.
              사이드바를 접으면 이 배지가 화면의 유일한 상태 표시가 되므로 그대로
              둘 수 없다. 다른 화면과 같은 맵을 쓴다(labels.ts).
              무엇의 상태인지도 밝힌다 — 아래 시료 상태 칩과 구별돼야 한다. */}
          <span
            data-testid="analysis-status-badge"
            title="분석 상태"
            className={`px-1.5 py-0.5 rounded-full text-xs font-medium shrink-0 ${analysisStatusColor(
              sampleInfoSummary.analysisStatus,
            )}`}
          >
            분석{" "}
            {analysisStatusLabels[sampleInfoSummary.analysisStatus] ??
              sampleInfoSummary.analysisStatus}
          </span>
          {/* 2026-08-28: 시료 유형·채취장소를 여기로 승격한다.
              좌측 사이드바(SampleInfo)의 5필드 중 이 헤더와 겹치지 않는 고유
              정보는 이 둘뿐이다 — 샘플 ID·이름은 축자 중복이고 상태는 위 배지가
              이미 말한다. 둘 다 분석당 고정된 참조 사실이라 한 번 읽으면 되는
              값이고, 여기 있으면 좌측을 접어도 잃는 정보가 없다.
              근거: docs/2026/260828_인수인계_사이드바접기_검토.md §5 · §6. */}
          {/* 시료 상태는 분석 상태와 다른 사실이다 — 시료가 failed 인데 나중 분석이
              completed 로 끝나는 조합이 실제로 있다. 좌측을 접으면 시료 상태는
              화면에서 사라지므로 여기로 함께 올린다. */}
          {sampleInfoSummary.sampleStatus && (
            <span
              data-testid="sample-status-chip"
              title="시료 상태"
              className={`shrink-0 px-1.5 py-0.5 rounded-full text-xs font-medium ${sampleStatusColor(
                sampleInfoSummary.sampleStatus,
              )}`}
            >
              시료{" "}
              {sampleStatusLabels[sampleInfoSummary.sampleStatus] ??
                sampleInfoSummary.sampleStatus}
            </span>
          )}
          {sampleInfoSummary.sampleType && (
            <span data-testid="sample-type-chip" className={`shrink-0 ${CHIP}`}>
              {sampleTypeLabels[sampleInfoSummary.sampleType] ??
                sampleInfoSummary.sampleType}
            </span>
          )}
          {sampleInfoSummary.collectionLocation && (
            <span
              data-testid="collection-location-chip"
              className={`max-w-[12rem] truncate ${CHIP}`}
              title={`채취장소: ${sampleInfoSummary.collectionLocation}`}
            >
              채취 {sampleInfoSummary.collectionLocation}
            </span>
          )}
        </div>
      )}
      <span className="text-xs text-gray-400 whitespace-nowrap">
        분석 ID: {analysisId}
      </span>
    </div>
  );
}
